import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Save, Lock } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, flagFor } from "@/lib/countries";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", job_title: "", phone: "", country: "" });
  const [savedAt, setSavedAt] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "",
      job_title: user.job_title || "",
      phone: user.phone || "",
      country: user.country || "",
    });
  }, [user]);

  const save = async () => {
    try {
      const { data } = await api.patch("/users/me", form);
      setUser(data);
      toast.success("Profile updated");
      setSavedAt(Date.now());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, avatar_filename: data.avatar_filename });
      toast.success("Profile picture updated");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  if (!user) return null;
  const flag = flagFor(user.country);
  const avatarUrl = user.avatar_filename
    ? `${API}/users/${user.id}/avatar?t=${savedAt || ""}`
    : null;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <div className="label-eyebrow">PROFILE</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Your account</h1>
      </div>

      {/* Avatar + identity */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-6 flex flex-col sm:flex-row gap-6 items-start">
        <div className="relative">
          <div className="h-28 w-28 border border-[#3b2bd0] bg-[#100755] grid place-items-center overflow-hidden">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="font-heading text-5xl font-bold uppercase text-white">{user.name?.[0]}</span>
            )}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="avatar-upload-btn"
            className="absolute -bottom-3 -right-3 h-10 w-10 grid place-items-center bg-white text-black border border-[#3b2bd0]"
            title="Change photo"
          >
            <Camera className="h-4 w-4" />
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onAvatar} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-2xl uppercase tracking-tight">
            {user.name} {flag && <span className="ml-2">{flag}</span>}
          </div>
          <div className="font-mono text-sm text-[#888] mt-1">{user.email}</div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-mono">
            <span className="border border-[#222] px-2 py-1">ROLE · {user.role}</span>
            {user.job_title && <span className="border border-[#222] px-2 py-1">{user.job_title}</span>}
            {user.country && <span className="border border-[#222] px-2 py-1">{user.country}</span>}
          </div>
          {uploading && <div className="text-xs font-mono text-[#888] mt-2">Uploading…</div>}
        </div>
      </Card>

      {/* Editable */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-6">
        <div className="label-eyebrow mb-4">EDIT DETAILS</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="NAME" value={form.name} set={(v) => setForm({ ...form, name: v })} testid="profile-name" />
          <Field label="JOB TITLE" value={form.job_title} set={(v) => setForm({ ...form, job_title: v })} testid="profile-title" />
          <Field label="PHONE" value={form.phone} set={(v) => setForm({ ...form, phone: v })} testid="profile-phone" />
          <div>
            <Label className="label-eyebrow">LOCATION</Label>
            <Select value={form.country || "none"} onValueChange={(v) => setForm({ ...form, country: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="profile-country" className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono">
                <SelectValue placeholder="Select country…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none max-h-72">
                <SelectItem value="none">— None —</SelectItem>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    <span className="font-mono flex items-center gap-2">{flagFor(c.code)}{c.code} — {c.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <Button onClick={save} data-testid="profile-save" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">
            <Save className="h-4 w-4 mr-2" /> Save changes
          </Button>
          <span className="text-xs font-mono text-[#666] flex items-center gap-1">
            <Lock className="h-3 w-3" /> Email & role can only be changed by an admin.
          </span>
        </div>
      </Card>

      {/* Security */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-6 flex items-center justify-between gap-4">
        <div>
          <div className="label-eyebrow">SECURITY</div>
          <div className="text-sm text-[#aaa] mt-1">Rotate your password regularly. Min 8 characters, bcrypt hashed.</div>
        </div>
        <Button
          variant="ghost"
          onClick={() => (window.location.href = "/change-password")}
          data-testid="profile-change-password"
          className="rounded-none border border-[#222] font-heading uppercase text-xs"
        >
          <Lock className="h-3 w-3 mr-2" /> Change password
        </Button>
      </Card>
    </div>
  );
}

function Field({ label, value, set, testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input value={value || ""} onChange={(e) => set(e.target.value)} data-testid={testid} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" />
    </div>
  );
}
